import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, Mail } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState } from "../../components/Common";
import { ADMIN_MENU } from "../../lib/menus";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import api, { apiError } from "../../lib/api";

const EMPTY = { name: "", username: "", email: "", password: "" };

export default function AdminAdmins() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(null); // {} for create, {id,...} for edit
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/admins").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { document.title = "Kelola Admin - DS KODE"; load(); }, []);

  const openCreate = () => { setForm(EMPTY); setModal({}); };
  const openEdit = (a) => { setForm({ name: a.name, username: a.username, email: a.email, password: "" }); setModal(a); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal.id) {
        const payload = { name: form.name, username: form.username, email: form.email };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/admins/${modal.id}`, payload);
        toast.success("Admin diperbarui");
      } else {
        await api.post("/admin/admins", form);
        toast.success("Admin ditambahkan");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Hapus admin ${a.name}?`)) return;
    try { await api.delete(`/admin/admins/${a.id}`); toast.success("Admin dihapus"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <DashboardLayout title="Kelola Admin" menu={ADMIN_MENU} testid="admin-admins">
      <div className="flex justify-end mb-5">
        <button onClick={openCreate} data-testid="add-admin-btn" className="btn-primary"><Plus className="h-4 w-4" /> Tambah Admin</button>
      </div>

      {rows === null ? <div className="h-48 skeleton rounded-xl" /> : rows.length === 0 ? <EmptyState icon={ShieldCheck} title="Belum ada admin." /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="th">Nama</th><th className="th">Username</th><th className="th">Email</th><th className="th">Dibuat</th><th className="th text-right">Aksi</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((a) => (
                  <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                    <td className="td font-medium text-slate-900">{a.name}{a.id === user?.id && <span className="ml-2 text-xs text-indigo-600 font-semibold">(Anda)</span>}</td>
                    <td className="td">{a.username}</td>
                    <td className="td"><span className="inline-flex items-center gap-1.5 text-slate-600"><Mail className="h-3.5 w-3.5 text-slate-400" /> {a.email}</span></td>
                    <td className="td text-slate-500">{formatDate(a.created_at)}</td>
                    <td className="td">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(a)} title="Edit" data-testid={`edit-admin-${a.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(a)} title="Hapus" disabled={a.id === user?.id} data-testid={`delete-admin-${a.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] grid place-items-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={() => setModal(null)}>
          <form onSubmit={save} className="bg-white rounded-2xl p-6 max-w-md w-full space-y-3.5" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
            <h3 className="font-heading font-bold text-slate-900">{modal.id ? "Edit Admin" : "Tambah Admin"}</h3>
            <div><label className="text-sm font-medium text-slate-700">Nama Lengkap</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input mt-1" data-testid="admin-name" /></div>
            <div><label className="text-sm font-medium text-slate-700">Username</label><input required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="input mt-1" data-testid="admin-username" /></div>
            <div><label className="text-sm font-medium text-slate-700">Email</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input mt-1" data-testid="admin-email" /></div>
            <div>
              <label className="text-sm font-medium text-slate-700">Password {modal.id && <span className="text-slate-400 font-normal">(kosongkan jika tidak diubah)</span>}</label>
              <input type="password" required={!modal.id} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input mt-1" placeholder="Minimal 8 karakter" data-testid="admin-password" />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setModal(null)} className="btn-outline flex-1">Batal</button>
              <button type="submit" className="btn-primary flex-1" data-testid="admin-save">Simpan</button>
            </div>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
}
